/**
 * LayoutMapPage — carnival layout from scan data on Google Maps satellite.
 *
 * Uses transit_matrix walking times + MDS to position attractions, then
 * projects them onto a real satellite view of the venue via Google Maps.
 * Falls back to the SVG TransitMap when venue coordinates aren't available.
 */

import { useState, lazy, Suspense, Component, type ReactNode, type ErrorInfo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useIntelligence } from '@/hooks/useIntelligence'
import { TransitMap } from '@/components/analytics/TransitMap'
import { PageLayout } from '@/components/PageLayout'
import { MapPin, Satellite, Grid3X3 } from 'lucide-react'

const VenueMap = lazy(() => import('@/components/analytics/VenueMap').then(m => ({ default: m.VenueMap })))
import { VENUE_COORDINATES } from '@/data/venue-coordinates'

class MapErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(_error: Error, _info: ErrorInfo) {
    console.warn('[MapErrorBoundary] VenueMap crashed:', _error)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-[500px] rounded-lg bg-surface text-center">
          <MapPin className="h-10 w-10 text-ink-soft/30 mb-3" />
          <h3 className="text-sm font-semibold text-ink-dark">Map unavailable</h3>
          <p className="text-xs text-ink-soft mt-1">
            The satellite map failed to load. Try switching to Diagram view.
          </p>
        </div>
      )
    }
    return this.props.children
  }
}

export function LayoutMapPage() {
  const { currentEventId, events } = useAuth()
  const { data, loading } = useIntelligence(currentEventId)
  const [viewMode, setViewMode] = useState<'satellite' | 'diagram'>('satellite')

  const event = events.find(e => e.EventID === currentEventId)
  const venueCity = event?.VenueCity || ''
  const hasVenueCoords = !!VENUE_COORDINATES[venueCity.toLowerCase()]

  if (loading) {
    return (
      <PageLayout title="Layout Map">
        <div className="space-y-4 p-6">
          <div className="h-8 w-64 animate-pulse rounded bg-surface" />
          <div className="h-[500px] animate-pulse rounded-lg bg-surface" />
        </div>
      </PageLayout>
    )
  }

  if (!data) {
    return (
      <PageLayout title="Layout Map">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <MapPin className="h-12 w-12 text-ink-soft/30 mb-4" />
          <h3 className="text-sm font-semibold text-ink-dark">No map data available</h3>
          <p className="text-xs text-ink-soft mt-1 max-w-sm">
            Select an event with scan data to see the auto-generated carnival layout.
          </p>
        </div>
      </PageLayout>
    )
  }

  if (!data.transit_graph || data.transit_graph.nodes.length === 0) {
    return (
      <PageLayout title="Layout Map">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <MapPin className="h-12 w-12 text-ink-soft/30 mb-4" />
          <h3 className="text-sm font-semibold text-ink-dark">No transit data for this event</h3>
          <p className="text-xs text-ink-soft mt-1 max-w-sm">
            Transit matrix data hasn't been computed for {event?.EventName ?? 'this event'} yet.
          </p>
        </div>
      </PageLayout>
    )
  }

  const effectiveMode = hasVenueCoords ? viewMode : 'diagram'

  return (
    <PageLayout
      title="Layout Map"
      actions={
        <div className="flex items-center gap-1 rounded-lg bg-surface p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('satellite')}
            disabled={!hasVenueCoords}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              effectiveMode === 'satellite'
                ? 'bg-white text-ink-dark shadow-sm'
                : 'text-ink-soft hover:text-ink'
            } ${!hasVenueCoords ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <Satellite className="h-3.5 w-3.5" />
            Satellite
          </button>
          <button
            type="button"
            onClick={() => setViewMode('diagram')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              effectiveMode === 'diagram'
                ? 'bg-white text-ink-dark shadow-sm'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            <Grid3X3 className="h-3.5 w-3.5" />
            Diagram
          </button>
        </div>
      }
    >
      <div className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-dark">
            {event?.EventName ?? 'Event'} — Layout Map
          </h2>
          <p className="text-xs text-ink-soft mt-0.5">
            {effectiveMode === 'satellite'
              ? 'Attraction positions overlaid on venue satellite imagery. Derived from guest scan chain walking patterns.'
              : 'Attraction positions derived from guest scan chain walking times via multidimensional scaling.'}
            {' '}Click any attraction to see distances and transit times.
          </p>
        </div>

        {effectiveMode === 'satellite' ? (
          <MapErrorBoundary>
            <Suspense fallback={<div className="h-[600px] animate-pulse rounded-lg bg-surface" />}>
              <VenueMap graph={data.transit_graph} venueCity={venueCity} />
            </Suspense>
          </MapErrorBoundary>
        ) : (
          <TransitMap graph={data.transit_graph} />
        )}
      </div>
    </PageLayout>
  )
}
